import {ThumbsdownIcon, ThumbsupIcon} from '@primer/octicons-react'
import {IconButton, type IconButtonProps} from '@primer/react'
import {Dialog} from '@primer/react/deprecated'
import {useCallback, useMemo, useRef, useState} from 'react'

import type {CopilotFeedback} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {useChatMessage} from './ChatMessageContext'
import {FeedbackForm} from './FeedbackForm'
import {type FeedbackDialogRef, ResponseFeedbackDialog} from './ResponseFeedbackDialog'

export type FeedbackProps = {
  iconSize: IconButtonProps['size']
  returnFocusRef: React.RefObject<HTMLDivElement>
  setParentFocusZoneEnabled?: (enabled: boolean) => void
  onFeedbackSubmitted?: () => void
}

export const COPILOT_FEEDBACK_HEADER_ID = 'copilot-feedback-header'

export const Feedback = ({iconSize, returnFocusRef, onFeedbackSubmitted}: FeedbackProps) => {
  const {message} = useChatMessage()
  const messageId = message.id
  const threadId = message.threadID
  const manager = useChatManager()
  const {optedInToUserFeedback} = useChatState()
  const [showForm, setShowForm] = useState<boolean>(true)
  const [submittedFeedback, setSubmittedFeedback] = useState<'POSITIVE' | 'NEGATIVE' | undefined>(message.feedback)
  const [isOpen, setIsOpen] = useState(false)
  const useNewFeedbackDialog = copilotFeatureFlags.newFeedbackComponent
  const dialogRef = useRef<FeedbackDialogRef>(null)

  const onFeedbackGiven = useCallback(
    (feedbackGiven: CopilotFeedback) => {
      manager.handleFeedback(message, feedbackGiven)
    },
    [manager, message],
  )

  const feedback = useMemo(() => {
    return copilotFeatureFlags.immersiveSubthreading ? message.feedback : submittedFeedback
  }, [message.feedback, submittedFeedback])

  const onClose = useCallback(
    async (feedbackGiven?: string) => {
      returnFocusRef.current?.focus()
      await manager.service.sendFeedback({
        feedback: feedbackGiven || feedback,
        messageId,
        threadId,
      })
      setShowForm(false)
    },
    [returnFocusRef, manager.service, feedback, messageId, threadId],
  )

  const onCloseNew = useCallback(() => {
    setIsOpen(false)
    returnFocusRef.current?.focus()
  }, [returnFocusRef])

  const onSubmit = useCallback(() => {
    returnFocusRef.current?.focus()
    setShowForm(false)
  }, [returnFocusRef])

  return (
    <>
      {feedback ? (
        <IconButton
          aria-label={`${feedback === 'POSITIVE' ? 'positive' : 'negative'} feedback submitted`}
          icon={feedback === 'POSITIVE' ? ThumbsupIcon : ThumbsdownIcon}
          variant="invisible"
          size={iconSize}
          disabled
          sx={{
            color: 'fg.default',
            bg: 'canvas.subtle',
          }}
        />
      ) : (
        <>
          <IconButton
            disabled={!showForm}
            icon={ThumbsupIcon}
            aria-label={'Good response'}
            onClick={() => {
              if (copilotFeatureFlags.immersiveSubthreading) {
                onFeedbackGiven('POSITIVE')
              } else {
                setSubmittedFeedback('POSITIVE')
              }

              onFeedbackSubmitted?.()

              // Pass explicitly because the state won't be updated yet.
              void onClose('POSITIVE')

              if (useNewFeedbackDialog && optedInToUserFeedback) {
                dialogRef.current?.openDialog('POSITIVE')
              }
            }}
            variant="invisible"
            size={iconSize}
            className="feedback-action"
          />
          <IconButton
            disabled={!showForm}
            icon={ThumbsdownIcon}
            size={iconSize}
            aria-label={'Bad response'}
            onClick={() => {
              if (copilotFeatureFlags.immersiveSubthreading) {
                onFeedbackGiven('NEGATIVE')
              } else {
                setSubmittedFeedback('NEGATIVE')
              }

              onFeedbackSubmitted?.()

              if (useNewFeedbackDialog || !optedInToUserFeedback) {
                // Pass explicitly because the state won't be updated yet.
                void onClose('NEGATIVE')
              }

              if (optedInToUserFeedback) {
                if (useNewFeedbackDialog) {
                  dialogRef.current?.openDialog('NEGATIVE')
                } else {
                  setIsOpen(true)
                }
              }
            }}
            variant="invisible"
            className="feedback-action"
          />
        </>
      )}
      {useNewFeedbackDialog && (
        <ResponseFeedbackDialog
          ref={dialogRef}
          messageId={messageId}
          threadId={threadId}
          onSubmitted={onFeedbackGiven}
          onClose={onCloseNew}
        />
      )}
      {!useNewFeedbackDialog && feedback === 'NEGATIVE' && showForm && optedInToUserFeedback && (
        <Dialog
          returnFocusRef={returnFocusRef}
          isOpen={isOpen}
          onDismiss={() => {
            setIsOpen(false)
          }}
          aria-labelledby={COPILOT_FEEDBACK_HEADER_ID}
          sx={{overflowX: 'hidden', overflowY: 'auto'}}
        >
          <Dialog.Header id={COPILOT_FEEDBACK_HEADER_ID}>Provide additional feedback</Dialog.Header>
          <FeedbackForm
            feedbackType={feedback}
            onClose={onClose}
            onSubmit={onSubmit}
            formLabel={COPILOT_FEEDBACK_HEADER_ID}
          />
        </Dialog>
      )}
    </>
  )
}

try{ Feedback.displayName ||= 'Feedback' } catch {}